import React, { FC, useCallback, useMemo } from "react";
import { View } from "react-native";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button } from "@lookiero/sty-psp-ui";
import {
  CheckoutFeedbackProjection,
  CheckoutFeedbackQuestionProjection,
} from "../../../../../../projection/checkoutFeedback/checkoutFeedback";
import { CheckoutFeedbackQuestionType } from "../../../../../../projection/checkoutFeedback/checkoutFeedback.constants";
import { CheckoutQuestions } from "../../../../components/organisms/checkoutQuestions/CheckoutQuestions";
import { useCheckoutQuestionFeedback } from "../../../../components/organisms/checkoutQuestions/behaviors/useCheckoutQuestionFeedback";
import {
  CheckoutQuestionItemProvider,
  CheckoutQuestionItems,
} from "../../../../components/organisms/checkoutQuestions/behaviors/useCheckoutQuestionItem";
import { ButtonCheckoutQuestionItem } from "../../../../components/organisms/checkoutQuestions/components/buttonCheckoutQuestionItem/ButtonCheckoutQuestionItem";
import { HostDefaultCheckoutQuestionItem } from "../../../../components/organisms/checkoutQuestions/components/hostDefaultCheckoutQuestionItem/HostDefaultCheckoutQuestionItem";
import { HostSelectCheckoutQuestionItem } from "../../../../components/organisms/checkoutQuestions/components/hostSelectCheckoutQuestionItem/HostSelectCheckoutQuestionItem";
import { IconCheckoutQuestionItem } from "../../../../components/organisms/checkoutQuestions/components/iconCheckoutQuestionItem/IconCheckoutQuestionItem";
import { TextareaCheckoutQuestionItem } from "../../../../components/organisms/checkoutQuestions/components/textareaCheckoutQuestionItem/TextareaCheckoutQuestionItem";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";

const checkoutQuestionItems: CheckoutQuestionItems = {
  [CheckoutFeedbackQuestionType.HOST_DEFAULT]: HostDefaultCheckoutQuestionItem,
  [CheckoutFeedbackQuestionType.HOST_TEXTAREA]: HostDefaultCheckoutQuestionItem,
  [CheckoutFeedbackQuestionType.HOST_SELECT]: HostSelectCheckoutQuestionItem,
  [CheckoutFeedbackQuestionType.TEXTAREA]: TextareaCheckoutQuestionItem,
  [CheckoutFeedbackQuestionType.ICON]: IconCheckoutQuestionItem,
  [CheckoutFeedbackQuestionType.BUTTON]: ButtonCheckoutQuestionItem,
};

interface CheckoutQuestionsFormProps {
  readonly checkoutQuestions: CheckoutFeedbackQuestionProjection[];
  readonly submitButtonDisabled: boolean;
  readonly onSubmit: (feedback: CheckoutFeedbackProjection) => void;
}
const CheckoutQuestionsForm: FC<CheckoutQuestionsFormProps> = ({
  checkoutQuestions,
  submitButtonDisabled,
  onSubmit,
}) => {
  const buttonText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.FEEDBACK_BUTTON });

  const feedback = useCheckoutQuestionFeedback();
  const handlePress = useCallback(() => onSubmit(feedback), [feedback, onSubmit]);

  const filteredCheckoutQuestions = useMemo(() => {
    const responses = Object.values(feedback);

    return checkoutQuestions.filter(
      (checkoutQuestion) =>
        // @ts-ignore
        !checkoutQuestion?.metadata?.showCondition ||
        // @ts-ignore
        checkoutQuestion?.metadata?.showCondition.length === 0 ||
        // @ts-ignore
        checkoutQuestion?.metadata?.showCondition.some((condition) => responses.includes(condition)),
    );
  }, [checkoutQuestions, feedback]);

  return (
    <View testID="checkout-questions-form">
      <CheckoutQuestionItemProvider checkoutQuestionItems={checkoutQuestionItems}>
        <CheckoutQuestions checkoutQuestions={filteredCheckoutQuestions} />
      </CheckoutQuestionItemProvider>

      <Button busy={submitButtonDisabled} testID="checkout-feedback-button" onPress={handlePress}>
        {buttonText}
      </Button>
    </View>
  );
};

export { CheckoutQuestionsForm };
